/**
 * FilterContext.tsx - Filter State Management Context
 *
 * Manages filter state, suggestions, and provides filter-related functionality.
 * Filters persist within the chat session but reset on page reload.
 * 
 */

import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect, useMemo } from 'react';
import { ActiveFilters, EMPTY_FILTERS, FilterSuggestions, DateRangeFilter } from '@/types/filter.types';
import { fetchFilterSuggestions, fetchVectorStoreOptions } from '@/api/filterApi';
import { useCircleContext } from './CircleContext';
import { useApi, useProfile } from 'core';
import { useTranslation } from 'react-i18next';
import { findLabelInHierarchicalTree, getNodeName } from '@/utilities/deweyCodeParser';

interface FilterContextState {
    activeFilters: ActiveFilters;
    setActiveFilters: (filters: ActiveFilters) => void;
    filterSuggestions: FilterSuggestions | null;
    setFilterSuggestions: (suggestions: FilterSuggestions | null) => void;
    isFilterOpen: boolean;
    setIsFilterOpen: (open: boolean) => void;
    loadingSuggestions: boolean;
    setLoadingSuggestions: (loading: boolean) => void;
    resetFilters: () => void;
    getActiveFilterCount: () => number;
    hasActiveFilters: () => boolean;
    updateDateRange: (dateRange: DateRangeFilter) => void;
    updateAuthors: (authors: string[]) => void;
    updatePersons: (persons: string[]) => void;
    updateOrganizations: (organizations: string[]) => void;
    updateLocations: (locations: string[]) => void;
    updateCategoryCodes: (categoryCodes: string[]) => void;
    loadFilterSuggestions: (circleId: string, searchQuery?: string) => Promise<void>;
    categoryTreeJson: Record<string, any> | null;
    findCategoryLabel: (code: string) => string;
}

const FilterContext = createContext<FilterContextState | undefined>(undefined);

interface FilterProviderProps {
    children: ReactNode;
}

export const FilterProvider: React.FC<FilterProviderProps> = ({ children }) => {
    const { parentCircleId } = useCircleContext();
    const api = useApi();
    const { data: profile } = useProfile();
    const { i18n } = useTranslation();
    const language = i18n.language;

    const [activeFilters, setActiveFiltersState] = useState<ActiveFilters>(EMPTY_FILTERS);
    const [filterSuggestions, setFilterSuggestionsState] = useState<FilterSuggestions | null>(null);
    const [isFilterOpen, setIsFilterOpenState] = useState<boolean>(false);
    const [loadingSuggestions, setLoadingSuggestionsState] = useState<boolean>(false);
    const [categoryTreeJson, setCategoryTreeJson] = useState<Record<string, any> | null>(null);
    const [suggestionsError, setSuggestionsError] = useState<boolean>(false);
    const [categoryTreeError, setCategoryTreeError] = useState<boolean>(false);
    

    // Memoized setters
    const setActiveFilters = useCallback((filters: ActiveFilters) => {
        setActiveFiltersState(filters);
    }, []);

    const setFilterSuggestions = useCallback((suggestions: FilterSuggestions | null) => {
        setFilterSuggestionsState(suggestions);
    }, []);

    const setIsFilterOpen = useCallback((open: boolean) => {
        setIsFilterOpenState(open);
    }, []);

    const setLoadingSuggestions = useCallback((loading: boolean) => {
        setLoadingSuggestionsState(loading);
    }, []);


    const resetFilters = useCallback(() => {
        setActiveFiltersState(EMPTY_FILTERS);
    }, []);

    const getActiveFilterCount = useCallback((): number => {
        let count = 0;

        if (
            activeFilters.dateRange.customFrom ||
            activeFilters.dateRange.customTo
        ) {
            count += 1;
        }

        count += activeFilters.authors.length;
        count += activeFilters.persons.length;
        count += activeFilters.organizations.length;
        count += activeFilters.locations.length;
        count += activeFilters.categoryCodes.length;

        return count;
    }, [activeFilters]);

    const hasActiveFilters = useCallback((): boolean => {
        return getActiveFilterCount() > 0;
    }, [getActiveFilterCount]);

    const updateDateRange = useCallback((dateRange: DateRangeFilter) => {
        setActiveFiltersState((prev) => ({
            ...prev,
            dateRange,
        }));
    }, []);

    const updateAuthors = useCallback((authors: string[]) => {
        setActiveFiltersState((prev) => ({
            ...prev,
            authors,
        }));
    }, []);

    const updatePersons = useCallback((persons: string[]) => {
        setActiveFiltersState((prev) => ({
            ...prev,
            persons,
        }));
    }, []);

    const updateOrganizations = useCallback((organizations: string[]) => {
        setActiveFiltersState((prev) => ({
            ...prev,
            organizations,
        }));
    }, []);

    const updateLocations = useCallback((locations: string[]) => {
        setActiveFiltersState((prev) => ({
            ...prev,
            locations,
        }));
    }, []);

    const updateCategoryCodes = useCallback((categoryCodes: string[]) => {
        setActiveFiltersState((prev) => ({
            ...prev,
            categoryCodes,
        }));
    }, []);

    const loadFilterSuggestions = useCallback(async (circleId: string, searchQuery: string = '') => {
        if (!circleId) return;

        setLoadingSuggestionsState(true);
        try {
            // First, fetch the vector store options to get related circle IDs
            const relatedCircleIds = await fetchVectorStoreOptions(circleId);
            
            // Combine current circleId with related circle IDs
            const allCircleIds = [circleId, ...relatedCircleIds];
            
            // Then fetch filter suggestions with all circle IDs
            const data = await fetchFilterSuggestions({
                circleId: allCircleIds,
                maxBuckets: 100,
                searchQuery,
            });

            setFilterSuggestionsState({
                authors: data.authors || [],
                persons: data.persons || [],
                organizations: data.organizations || [],
                locations: data.locations || [],
                categoryCodes: data.categoryCodes || [],
                tags: data.tags || [],
            });
        } catch (error) {
            console.error('Failed to load filter suggestions:', error);
            setSuggestionsError(true);
        } finally {
            setLoadingSuggestionsState(false);
        }
    }, []);

    // Load vector search options (classification tree) for category label mapping
    const loadCategoryTree = useCallback(async (circleId: string) => {
        if (!circleId || categoryTreeJson) return;

        try {
            const data = await api.get({
                path: "/admin/options?type=classification&circleId=" + circleId,
            });

            if (data?.options?.json) {
                setCategoryTreeJson(data.options.json);
            }
        } catch (error) {
            console.error('Failed to load category tree:', error);
            setCategoryTreeError(true);
        }
    }, [api, categoryTreeJson]);

    // Recursive helper for direct key matching (original behavior)
    const findDirectMatch = useCallback(
        (code: string, node: Record<string, any>, lang?: string): string => {
            const search = (searchCode: string, searchNode: Record<string, any>): string => {
                for (const [key, value] of Object.entries(searchNode)) {
                    // Direct match
                    if (key === searchCode) {
                        const name = getNodeName(value, lang);
                        if (name) return name;
                    }

                    // Recurse into divisions first if present
                    if (value && typeof value === "object" && "divisions" in value) {
                        const found = search(searchCode, (value as any).divisions);
                        if (found !== searchCode) return found;
                    }

                    // Otherwise, recurse into the value itself (in case nested without "divisions" key)
                    if (value && typeof value === "object") {
                        const found = search(searchCode, value as Record<string, any>);
                        if (found !== searchCode) return found;
                    }
                }

                return searchCode;
            };
            return search(code, node);
        },
        [],
    );

    // Helper function to find category label from code
    // Supports hierarchical codes like "900.950" where the path is tree["900"].divisions["950"]
    const findCategoryLabel = useCallback(
        (code: string, node?: Record<string, any> | null): string => {
            const searchNode = node || categoryTreeJson;
            if (!searchNode || typeof searchNode !== "object") return code;

            // Strategy 1: Try hierarchical tree traversal for codes like "900.950", "600.610"
            // This handles the parent.child format where we need to navigate through divisions
            const hierarchicalResult = findLabelInHierarchicalTree(code, searchNode, language);
            if (hierarchicalResult !== code) return hierarchicalResult;

            // Strategy 2: Direct recursive search for simple codes or fallback
            const directResult = findDirectMatch(code, searchNode, language);
            if (directResult !== code) return directResult;

            return code;
        },
        [categoryTreeJson, findDirectMatch, language],
    );

    // Reset error states when user authenticates (allows retry after login)
    useEffect(() => {
        if (profile) {
            setSuggestionsError(false);
            setCategoryTreeError(false);
        }
    }, [profile]);

    // Load filter suggestions when parentCircleId becomes available (only for authenticated users)
    useEffect(() => {
        if (profile && parentCircleId && !filterSuggestions && !loadingSuggestions && !suggestionsError) {
            loadFilterSuggestions(parentCircleId);
        }
    }, [profile, parentCircleId, filterSuggestions, loadingSuggestions, loadFilterSuggestions, suggestionsError]);

    // Load category tree when parentCircleId becomes available (only for authenticated users)
    useEffect(() => {
        if (profile && parentCircleId && !categoryTreeJson && !categoryTreeError) {
            loadCategoryTree(parentCircleId);
        }
    }, [profile, parentCircleId, categoryTreeJson, loadCategoryTree, categoryTreeError]);

    // Memoize the entire context value
    const value = useMemo<FilterContextState>(() => ({
        activeFilters,
        setActiveFilters,
        filterSuggestions,
        setFilterSuggestions,
        isFilterOpen,
        setIsFilterOpen,
        loadingSuggestions,
        setLoadingSuggestions,
        resetFilters,
        getActiveFilterCount,
        hasActiveFilters,
        updateDateRange,
        updateAuthors,
        updatePersons,
        updateOrganizations,
        updateLocations,
        updateCategoryCodes,
        loadFilterSuggestions,
        categoryTreeJson,
        findCategoryLabel,
    }), [
        activeFilters,
        setActiveFilters,
        filterSuggestions,
        setFilterSuggestions,
        isFilterOpen,
        setIsFilterOpen,
        loadingSuggestions,
        setLoadingSuggestions,
        resetFilters,
        getActiveFilterCount,
        hasActiveFilters,
        updateDateRange,
        updateAuthors,
        updatePersons,
        updateOrganizations,
        updateLocations,
        updateCategoryCodes,
        loadFilterSuggestions,
        categoryTreeJson,
        findCategoryLabel,
    ]);

    return <FilterContext.Provider value={value}>{children}</FilterContext.Provider>;
};

export const useFilterContext = (): FilterContextState => {
    const context = useContext(FilterContext);
    if (context === undefined) {
        throw new Error('useFilterContext must be used within a FilterProvider');
    }
    return context;
};